/**
 * Jurídico × Compras (ADR 0070): o contrato com fornecedor (F05 nacional, F06 internacional) nasce
 * da compra, pelo serviço do Jurídico, e Compras lê só a situação mínima do contrato ligado.
 */
import { z } from 'zod';

/** Modalidades que só nascem de uma compra (origin_rule = purchasing_only). */
export const LEGAL_PURCHASING_SERVICES = ['F05', 'F06'] as const;
export type LegalPurchasingService = (typeof LEGAL_PURCHASING_SERVICES)[number];

/** Pedido de abertura feito a partir da compra; os dados da compra vêm do próprio cadastro. */
export const legalPurchaseRequestInput = z
  .object({
    purchaseRequestId: z.uuid(),
    serviceCode: z.enum(LEGAL_PURCHASING_SERVICES),
    /** Fornecedor escolhido (cadastro de parceiros); quem pede confirma no formulário. */
    partnerId: z.uuid().nullish(),
    /** Cotação ou proposta escolhida, como referência em texto. */
    quoteReference: z.string().trim().max(200).nullish(),
  })
  .strict();
export type LegalPurchaseRequestInput = z.infer<typeof legalPurchaseRequestInput>;

/**
 * Situações da compra que permitem abrir o contrato: a compra já foi aprovada (regra provisória da
 * Onda 2, 28/10/2026; antes da aprovação a contratação nem está decidida).
 */
export const LEGAL_PURCHASE_OPEN_STATUSES = ['approved', 'ordered', 'partially_delivered'] as const;

/** Autorização do Jurídico para o pedido sem contrato ativo: só o motivo vem da tela. */
export const legalOrderReleaseInput = z
  .object({ reason: z.string().trim().min(5).max(2000) })
  .strict();

/**
 * Regra de Compras para emitir o pedido de uma compra que exige contrato (combinada em 28/10/2026,
 * provisória): contrato ativo ou autorização do Jurídico.
 */
export function legalPurchaseOrderAllowed(items: LegalPurchaseContractStatus[]): boolean {
  return items.some((x) => x.contract?.status === 'active' || x.orderRelease !== null);
}

/** O que Compras recebe de cada solicitação originada pela compra (nada além disso). */
export interface LegalPurchaseContractStatus {
  requestId: string;
  serviceCode: string;
  protocol: string | null;
  requestStatus: string;
  linkedAt: string;
  contract: {
    code: string;
    status: string;
    signed: boolean;
    startDate: string | null;
    endDate: string | null;
    /** Valor do contrato como cadastrado (texto decimal), na moeda e periodicidade ao lado. */
    amount: string | null;
    currency: string | null;
    periodicity: string | null;
  } | null;
  /** Autorização do Jurídico para emitir o pedido sem contrato ativo (data e motivo). */
  orderRelease: { authorizedAt: string; reason: string } | null;
}
